"use client";

import { Link } from "@/base/component/link";
import { useAuth } from "@/base/controller/auth-controller";
import { appConfig } from "@/config";
import {
  Button,
  Checkbox,
  Divider,
  Form,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Tab,
  Tabs,
} from "@heroui/react";
import { IconBrandGoogle, IconEyeClosed } from "@tabler/icons-react";
import { useRouter } from "next/navigation";
import React from "react";

export default function Auth() {
  return (
    <div className="flex flex-col w-full items-center justify-center" id="auth">
      <Tabs>
        <Tab key="login" title="Login">
          <Login />
        </Tab>
        <Tab key="register" title="Register">
          <Register />
        </Tab>
      </Tabs>
    </div>
  );
}

const Login = () => {
  const [isVisible, setIsVisible] = React.useState(false);

  const toggleVisibility = () => setIsVisible(!isVisible);

  const authController = useAuth();
  const router = useRouter();

  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] =
    React.useState(false);
  const [resetPassword, setResetPassword] = React.useState<string | null>(null);
  const [loadingResetPassword, setLoadingResetPassword] = React.useState(false);

  return (
    <div className="flex w-full md:min-w-[400px] flex-col gap-4 rounded-large bg-content1 px-8 pb-10 pt-6 shadow-small">
      <div className="flex flex-col gap-1">
        <span className="text-large font-medium">Sign in to your account</span>
        <p className="text-small text-default-500">
          to continue with {appConfig.name}
        </p>
      </div>

      <Modal isOpen={error !== null} onClose={() => setError(null)}>
        <ModalContent>
          <ModalHeader>Authentication</ModalHeader>
          <ModalBody>Login failed. Please try again.</ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setError(null)}>
              OK
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isResetPasswordModalOpen}
        onClose={() => setIsResetPasswordModalOpen(false)}
      >
        <ModalContent>
          <ModalHeader>Forgot Password</ModalHeader>
          <ModalBody>
            <Input
              isRequired
              label="Email Address"
              name="email"
              placeholder="Enter your email"
              type="email"
              variant="bordered"
              value={resetPassword ?? undefined}
              onChange={(e) => setResetPassword(e.target.value)}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              isDisabled={!resetPassword}
              isLoading={loadingResetPassword}
              onPress={() => {
                if (resetPassword) {
                  setLoadingResetPassword(true);

                  authController
                    .resetPassword(resetPassword)
                    .then(() => {
                      setIsResetPasswordModalOpen(false);
                    })
                    .finally(() => {
                      setLoadingResetPassword(false);
                    });
                }
              }}
              aria-label="Send password reset email"
            >
              Send reset email
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Form
        className="flex flex-col gap-3"
        validationBehavior="native"
        onSubmit={async (event) => {
          event.preventDefault();

          setIsLoading(true);

          const formData = new FormData(event.target as HTMLFormElement);

          const email = formData.get("email") as string;
          const password = formData.get("password") as string;

          await authController
            .login(email, password)
            .then(() => {
              router.push("/platform/project");
            })
            .catch((error) => {
              setError(error.message);
            })
            .finally(() => {
              setIsLoading(false);
            });
        }}
      >
        <Input
          isRequired
          label="Email Address"
          name="email"
          placeholder="Enter your email"
          type="email"
          variant="bordered"
        />
        <Input
          isRequired
          endContent={
            <button
              type="button"
              onClick={toggleVisibility}
              aria-label="Toggle password visibility"
            >
              {isVisible ? (
                <IconEyeClosed
                  className="pointer-events-none text-2xl text-default-400"
                  aria-label="Hide password"
                  role="img"
                />
              ) : (
                <IconEyeClosed
                  className="pointer-events-none text-2xl text-default-400"
                  aria-label="Show password"
                  role="img"
                />
              )}
            </button>
          }
          label="Password"
          name="password"
          placeholder="Enter your password"
          type={isVisible ? "text" : "password"}
          variant="bordered"
        />
        <Button
          className="w-full"
          color="primary"
          type="submit"
          isLoading={isLoading}
          aria-label="Sign in to your account"
        >
          Sign In
        </Button>
      </Form>

      <div className="flex justify-end">
        <Link
          size="sm"
          onPress={() => setIsResetPasswordModalOpen(true)}
          aria-label="Forgot password?"
          className="cursor-pointer"
        >
          Forgot password?
        </Link>
      </div>

      <div className="flex items-center gap-4 py-2">
        <Divider className="flex-1" />
        <span className="text-sm text-default-500">OR</span>
        <Divider className="flex-1" />
      </div>

      <Button
        className="w-full"
        variant="bordered"
        startContent={<IconBrandGoogle size={18} />}
        onPress={() => authController.googleLogin()}
        aria-label="Sign in with Google"
      >
        Sign in with Google
      </Button>
    </div>
  );
};

const Register = () => {
  const [isVisible, setIsVisible] = React.useState(false);

  const toggleVisibility = () => setIsVisible(!isVisible);

  const authController = useAuth();
  const router = useRouter();

  const [error, setError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isChecked, setIsChecked] = React.useState(false);
  return (
    <div className="flex w-full md:min-w-[400px] flex-col gap-4 rounded-large bg-content1 px-8 pb-10 pt-6 shadow-small">
      <div className="flex flex-col gap-1">
        <span className="text-large font-medium">Create an account</span>
        <p className="text-small text-default-500">
          to continue to {appConfig.name}
        </p>
      </div>

      <Modal isOpen={error !== null} onClose={() => setError(null)}>
        <ModalContent>
          <ModalHeader>Authentication</ModalHeader>
          <ModalBody>{error ?? "Login failed. Please try again."}</ModalBody>
          <ModalFooter>
            <Button
              variant="light"
              onPress={() => setError(null)}
              aria-label="Close error message"
            >
              OK
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Form
        className="flex flex-col gap-3"
        validationBehavior="native"
        onSubmit={async (event) => {
          event.preventDefault();

          setIsLoading(true);

          const formData = new FormData(event.target as HTMLFormElement);

          const email = formData.get("email") as string;
          const password = formData.get("password") as string;
          const confirmPassword = formData.get("confirmPassword") as string;

          if (password !== confirmPassword) {
            setError("Passwords do not match");
            setIsLoading(false);
            return;
          }

          await authController
            .signup(email, password, confirmPassword)
            .then(() => {
              router.push("/platform/project");
            })
            .catch((error) => {
              setError(error.message);
            })
            .finally(() => {
              setIsLoading(false);
            });
        }}
      >
        <Input
          isRequired
          label="Email Address"
          name="email"
          placeholder="Enter your email"
          type="email"
          variant="bordered"
        />
        <Input
          isRequired
          endContent={
            <button
              type="button"
              onClick={toggleVisibility}
              aria-label="Toggle password visibility"
            >
              {isVisible ? (
                <IconEyeClosed
                  className="pointer-events-none text-2xl text-default-400"
                  aria-label="Hide password"
                  role="img"
                />
              ) : (
                <IconEyeClosed
                  className="pointer-events-none text-2xl text-default-400"
                  aria-label="Show password"
                  role="img"
                />
              )}
            </button>
          }
          label="Password"
          name="password"
          placeholder="Enter your password"
          type={isVisible ? "text" : "password"}
          variant="bordered"
        />
        <Input
          isRequired
          endContent={
            <button
              type="button"
              onClick={toggleVisibility}
              aria-label="Toggle password visibility"
            >
              {isVisible ? (
                <IconEyeClosed
                  className="pointer-events-none text-2xl text-default-400"
                  aria-label="Hide password"
                  role="img"
                />
              ) : (
                <IconEyeClosed
                  className="pointer-events-none text-2xl text-default-400"
                  aria-label="Show password"
                  role="img"
                />
              )}
            </button>
          }
          label="Password"
          name="confirmPassword"
          placeholder="Confirm your password"
          type={isVisible ? "text" : "password"}
          variant="bordered"
        />

        <Button
          className="w-full"
          color="primary"
          type="submit"
          isLoading={isLoading}
          aria-label="Create new account"
        >
          Sign Up
        </Button>

        <div className="flex items-center gap-4 py-2 w-full">
          <Divider className="flex-1" />
          <span className="text-sm text-default-500">OR</span>
          <Divider className="flex-1" />
        </div>

        <Button
          className="w-full"
          variant="bordered"
          isDisabled={!isChecked}
          startContent={<IconBrandGoogle size={18} />}
          onPress={() => authController.googleLogin()}
          aria-label="Sign up with Google"
        >
          Sign up with Google
        </Button>

        <div className="flex w-full py-2">
          <Divider className="h-px w-full" />
        </div>

        <div className="flex flex-row gap-1">
          <Checkbox
            checked={isChecked}
            onChange={(e) => setIsChecked(e.target.checked)}
            isRequired
            size="sm"
          ></Checkbox>
          <div className="block text-sm">
            <span>I agree with the </span>
            <Link
              href="/docs/terms-of-service/index.html"
              target="_blank"
              size="sm"
            >
              Terms
            </Link>
            <span> and </span>
            <Link
              href="/docs/privacy-policy/index.html"
              target="_blank"
              size="sm"
            >
              Privacy Policy
            </Link>
          </div>
        </div>
      </Form>
    </div>
  );
};
